import type { Language } from "../types";
import { business } from "../config/business";
import pageData from "./publicPages.json";

export interface SeoPage {
  path: string;
  titleAr: string;
  titleEn: string;
  descriptionAr: string;
  descriptionEn: string;
  canonical: string;
  index: boolean;
  preferredInitialLanguage: Language;
  schemaType: "WebPage" | "CollectionPage" | "AboutPage" | "ContactPage";
  h1IntentAr: string;
  h1IntentEn: string;
}

export const publicSeoPages = pageData as SeoPage[];

export const siteName = `${business.businessNameEn} | ${business.businessNameAr}`;
export const socialImageUrl = `${business.domain}${business.assets.storefront}`;
export const logoUrl = `${business.domain}${business.assets.logo}`;

const normalizedPath = (pathname: string) => {
  const cleanPath = pathname.split("?")[0].replace(/\/+$/, "");
  return cleanPath || "/";
};

export const getSeoPage = (pathname: string) =>
  publicSeoPages.find((page) => page.path === normalizedPath(pathname));

export const pageText = (page: SeoPage, lang: Language) => ({
  title: lang === "ar" ? page.titleAr : page.titleEn,
  description: lang === "ar" ? page.descriptionAr : page.descriptionEn,
  imageAlt:
    lang === "ar"
      ? `واجهة ${business.businessNameAr} في عمّان`
      : `${business.businessNameEn} storefront in Amman`,
  locale: lang === "ar" ? "ar_JO" : "en_US",
  alternateLocale: lang === "ar" ? "en_US" : "ar_JO",
  inLanguage: lang === "ar" ? "ar-JO" : "en-US",
});

export const canonicalForPath = (pathname: string) => {
  const page = getSeoPage(pathname);
  return page?.canonical || `${business.domain}${normalizedPath(pathname)}`;
};

export const robotsForPage = (page?: SeoPage) =>
  page?.index ? "index,follow,max-image-preview:large" : "noindex,follow";

export const buildStructuredData = (page: SeoPage, lang: Language) => {
  const text = pageText(page, lang);
  const webpageId =
    page.path === "/"
      ? `${business.domain}/#webpage`
      : `${page.canonical}#webpage`;

  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Pharmacy",
        "@id": `${business.domain}/#pharmacy`,
        name: business.businessNameEn,
        alternateName: business.businessNameAr,
        url: `${business.domain}/`,
        telephone: business.phone,
        email: business.email,
        logo: logoUrl,
        image: socialImageUrl,
        address: {
          "@type": "PostalAddress",
          streetAddress: business.streetAddressEn,
          addressLocality: business.cityEn,
          addressCountry: business.countryCode,
        },
        geo: {
          "@type": "GeoCoordinates",
          latitude: business.latitude,
          longitude: business.longitude,
        },
        hasMap: business.mapsUrl,
        openingHoursSpecification: {
          "@type": "OpeningHoursSpecification",
          dayOfWeek: [
            "Monday",
            "Tuesday",
            "Wednesday",
            "Thursday",
            "Friday",
            "Saturday",
            "Sunday",
          ],
          opens: business.schemaOpens,
          closes: business.schemaCloses,
        },
        sameAs: [
          business.facebookUrl,
          business.instagramUrl,
          business.threadsUrl,
        ],
      },
      {
        "@type": "WebSite",
        "@id": `${business.domain}/#website`,
        url: `${business.domain}/`,
        name: siteName,
        inLanguage: ["ar-JO", "en-US"],
        publisher: { "@id": `${business.domain}/#pharmacy` },
      },
      {
        "@type": page.schemaType,
        "@id": webpageId,
        url: page.canonical,
        name: text.title,
        description: text.description,
        inLanguage: text.inLanguage,
        isPartOf: { "@id": `${business.domain}/#website` },
        about: { "@id": `${business.domain}/#pharmacy` },
      },
    ],
  };
};
